import i18next from 'i18next';
import type { ItemV2 } from '@/common/api/types/items';
import type {
  ICreatedByHeader,
  ICustomFieldHeader,
  IEstimationHeader,
  IItemStatusHeader,
  IParentItemHeader,
  IPlanHeader,
  ISwimlaneHeader,
  ISwimlaneHeaderProps,
  ITeamHeader,
  IVisualizationHeaderProps,
} from '@/common/api/types/loopVisualization';
import {
  isVisualizationHeaderCustomField,
  isVisualizationHeaderEstimation,
  isVisualizationHeaderItemStatus,
  isVisualizationHeaderPlan,
  isVisualizationHeaderTeam,
} from '@/features/visualizations/components/layout/header-menu/utils';
import { generateDateHeaders } from '../swimlane/utils/dateHeaderUtils';
import { NO_VALUE_ID } from './constants';

function generateCustomPropertyFieldHeaders({
  options,
  customFieldType,
  includeCustomerDisplayValues,
  ...selector
}: (ICustomFieldHeader | ITeamHeader | IItemStatusHeader | IPlanHeader | IEstimationHeader) & {
  includeCustomerDisplayValues: boolean;
}) {
  const headers: ISwimlaneHeaderProps[] = options.map((option) => ({
    label: 'name' in option ? option.name : option.label || option.id.toString(),
    id: option.id.toString(),
    ...(customFieldType ? { customFieldType } : {}),
    ...(includeCustomerDisplayValues && customFieldType === 'MULTI_CUSTOMER'
      ? {
          displayValues: [
            {
              type: 'customer' as const,
              id: option.id.toString(),
              name: 'name' in option ? option.name : option.id.toString(),
              logoUrl: 'logoUrl' in option ? option.logoUrl : undefined,
            },
          ],
        }
      : {}),
    selector,
    headerColor: '#1c1c1d',
    isDateHeader: false,
  }));
  headers.push({
    label: i18next.t(($) => $.loopVisualization.layout.headers.fields.noValue),
    id: NO_VALUE_ID,
    selector,
    headerColor: '#1c1c1d',
    isDateHeader: false,
  });

  return headers;
}

function generateCreatedByHeaders(
  { options, ...selector }: ICreatedByHeader,
  includeNullHeader: boolean,
) {
  const headers = options.map((user) => ({
    label:
      user.name ||
      user.email ||
      i18next.t(($) => $.loopVisualization.layout.headers.fields.unknownUser),
    id: user.id,
    selector,
    headerColor: '#1c1c1d',
    isDateHeader: false,
  }));

  if (includeNullHeader) {
    headers.push({
      label: i18next.t(($) => $.loopVisualization.layout.headers.fields.noValue),
      id: NO_VALUE_ID,
      selector,
      headerColor: '#1c1c1d',
      isDateHeader: false,
    });
  }

  return headers;
}

function generateItemParentHeader(
  { options, ...selector }: IParentItemHeader,
  includeNullHeader: boolean,
) {
  const headers = options.map((option) => ({
    label: option.name,
    id: option.id.toString(),
    selector,
    headerColor: '#1c1c1d',
    isDateHeader: false,
  }));

  if (includeNullHeader) {
    headers.push({
      label: i18next.t(($) => $.loopVisualization.layout.headers.fields.noValue),
      id: NO_VALUE_ID,
      selector,
      headerColor: '#1c1c1d',
      isDateHeader: false,
    });
  }

  return headers;
}

export function generateHeaders(params: {
  header: ISwimlaneHeader | null;
  items: ItemV2[];
  includeNullHeader?: boolean;
  includeDateHeader?: false;
  includeCustomerDisplayValues?: boolean;
}): IVisualizationHeaderProps[];
export function generateHeaders(params: {
  header: ISwimlaneHeader | null;
  items: ItemV2[];
  includeNullHeader?: boolean;
  includeDateHeader: true;
  includeCustomerDisplayValues?: boolean;
}): ISwimlaneHeaderProps[];
export function generateHeaders({
  header,
  items,
  includeNullHeader = false,
  includeDateHeader = false,
  includeCustomerDisplayValues = true,
}: {
  header: ISwimlaneHeader | null;
  items: ItemV2[];
  includeNullHeader?: boolean;
  includeDateHeader?: boolean;
  includeCustomerDisplayValues?: boolean;
}): IVisualizationHeaderProps[] | ISwimlaneHeaderProps[] {
  if (!header || (!includeDateHeader && header.type === 'DateSelector')) {
    return [];
  }

  switch (header.type) {
    case 'ParentItemSelector':
      return generateItemParentHeader(header, includeNullHeader);
    case 'CustomPropertySelectorByID': {
      if (
        isVisualizationHeaderTeam(header) ||
        isVisualizationHeaderCustomField(header) ||
        isVisualizationHeaderItemStatus(header) ||
        isVisualizationHeaderPlan(header) ||
        isVisualizationHeaderEstimation(header)
      ) {
        return generateCustomPropertyFieldHeaders({ ...header, includeCustomerDisplayValues });
      }
      return [];
    }
    case 'DateSelector': {
      return generateDateHeaders(header, items, includeNullHeader);
    }
    case 'createdBy': {
      return generateCreatedByHeaders(header, includeNullHeader);
    }
    default:
      return [];
  }
}
